import React, { useEffect, useContext, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { FiBell, FiClock } from "react-icons/fi";
import { FaStickyNote } from "react-icons/fa";
import Header from "../../common/header/header";
import axios from "axios";
import AuthContext from "../../auth/auth-context";
import { format, parseISO, isValid } from "date-fns";
import {
  PAYMENT_NOTIFICATION_TABLE_COLUMNS,
  PAYMENT_NOTIFICATION_DETAIL_SECTIONS,
} from "./payment-notification.model";
import styles from "./payment-notification.module.css";
import PaymentNotificationNotes from "./payment-notification-notes";
import Loader from "../../components/loader/loader";
import "../../components/table/generic-table.css";

// Styled layout aligned with trade-view.js (TradeView)
const Container = styled.div`
  max-width: 980px;
`;

const Title = styled.h2`
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 800;
  font-size: 1.25rem;
  line-height: 1.2;
  letter-spacing: -0.2px;
  color: #212529;
  position: relative;
  padding-bottom: 8px;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 56px;
    height: 3px;
    border-radius: 999px;
    background: linear-gradient(90deg, #0d6efd 0%, #6f42c1 100%);
  }

  svg {
    color: #0d6efd;
    flex-shrink: 0;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
  overflow: hidden;
  margin-bottom: 16px;
`;

const ModalCard = styled(Card)`
  margin-bottom: 0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const PairBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 0.95rem;
  color: #212529;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  padding: 6px 10px;
  border-radius: 999px;
`;

const Meta = styled.span`
  color: #6c757d;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Strong = styled.strong`
  color: #212529;
`;

const Body = styled.div`
  border-top: 1px solid #e9ecef;
  padding: 12px 16px 14px;
  background: #ffffff;
`;

const DetailSection = styled.div`
  &:not(:first-of-type) {
    margin-top: 22px;
    padding-top: 20px;
    border-top: 1px solid #e9ecef;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 12px;
  color: #212529;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Row = styled.div`
  display: contents;
`;

const Label = styled.span`
  color: #6c757d;
  padding: 6px 0;
`;

const Value = styled.span`
  color: #212529;
  font-weight: 600;
  padding: 6px 0;
  word-break: break-word;
`;

const ModalBodyPanel = styled.div`
  background: #f8f9fa;
  padding: 16px 18px 20px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  width: 100%;
`;

function normalizeList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  return [];
}

const PAGE_SIZE = 15;

function getCreatedAtTimestamp(row) {
  const raw = row?.created_at;
  if (!raw) return 0;
  try {
    const d = parseISO(String(raw));
    return isValid(d) ? d.getTime() : 0;
  } catch {
    return 0;
  }
}

function sortByCreatedAtNewestFirst(list) {
  return [...list].sort(
    (a, b) => getCreatedAtTimestamp(b) - getCreatedAtTimestamp(a)
  );
}

function formatCellValue(key, raw) {
  if (raw === null || raw === undefined || raw === "") return "—";
  if (key === "is_deleted" || key === "is_actioned") {
    if (raw === true) return "Yes";
    if (raw === false) return "No";
    return "—";
  }
  if (key === "last_updated" || key === "created_at") {
    try {
      const d = parseISO(String(raw));
      if (isValid(d)) return format(d, "dd/MM/yyyy HH:mm");
    } catch {
      /* fall through */
    }
  }
  return String(raw);
}

function ActionedTableBadge({ value }) {
  if (value === true) {
    return (
      <span className={`${styles.actionedBadge} ${styles.actionedBadgeYes}`}>
        Yes
      </span>
    );
  }
  if (value === false) {
    return (
      <span className={`${styles.actionedBadge} ${styles.actionedBadgeNo}`}>
        No
      </span>
    );
  }
  return (
    <span
      className={`${styles.actionedBadge} ${styles.actionedBadgeUnknown}`}
    >
      —
    </span>
  );
}

ActionedTableBadge.propTypes = {
  value: PropTypes.bool,
};

function formatClockLabel(row) {
  const raw = row?.created_at;
  if (raw === null || raw === undefined || raw === "") return "—";
  try {
    const d = parseISO(String(raw));
    if (isValid(d)) return format(d, "yyyy-MM-dd HH:mm");
  } catch {
    /* fall through */
  }
  return String(raw);
}

function amountBadgeText(row) {
  const c = row?.currency;
  const a = row?.amount;
  if (c && a) return `${c} ${a}`;
  if (c) return c;
  if (a) return a;
  return "—";
}

function getNotesCount(row, noteCountsById = {}) {
  if (row?.id != null && typeof noteCountsById[row.id] === "number") {
    return noteCountsById[row.id];
  }
  if (typeof row?.notes_total === "number") return row.notes_total;
  if (typeof row?.note_count === "number") return row.note_count;
  if (Array.isArray(row?.notes)) return row.notes.length;
  return 0;
}

function NotesIndicator({ count }) {
  const hasNotes = count > 0;
  return (
    <span
      className={`${styles.notesIndicator} ${
        hasNotes ? styles.notesIndicatorActive : styles.notesIndicatorEmpty
      }`}
      aria-label={`${count} note${count === 1 ? "" : "s"}`}
    >
      <FaStickyNote aria-hidden />
      <span className={styles.notesCount}>{count}</span>
    </span>
  );
}

NotesIndicator.propTypes = {
  count: PropTypes.number.isRequired,
};

const TAB_INCOMPLETE = "incomplete";
const TAB_COMPLETED = "completed";
const SEARCH_DEBOUNCE_MS = 300;

const PaymentNotification = () => {
  const authCtx = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [noteCountsById, setNoteCountsById] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState(TAB_INCOMPLETE);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const sortedItems = useMemo(
    () => sortByCreatedAtNewestFirst(items),
    [items]
  );
  const pageCount = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedItems.slice(start, start + PAGE_SIZE);
  }, [sortedItems, currentPage]);
  const pageStart =
    sortedItems.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(currentPage * PAGE_SIZE, sortedItems.length);

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleNotesChange = useCallback((notificationId, count) => {
    if (notificationId == null) return;
    setNoteCountsById((prev) => ({ ...prev, [notificationId]: count }));
    setItems((prev) =>
      prev.map((row) =>
        row.id === notificationId ? { ...row, notes_total: count } : row
      )
    );
  }, []);

  const fetchPaymentNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        is_actioned: activeTab === TAB_COMPLETED ? "true" : "false",
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/dealer/investec-notifications/`,
        {
          params,
          headers: {
            Authorization: `JWT ${authCtx?.token}`,
            "Acting-as": "dealer",
          },
        }
      );
      setItems(normalizeList(response.data));
      setCurrentPage(1);
    } catch (err) {
      console.error("Error fetching payment notifications", err);
      setError(
        err.response?.data?.detail ||
        err.message ||
        "Could not load payment notifications."
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [authCtx?.token, activeTab, debouncedSearch]);

  useEffect(() => {
    fetchPaymentNotifications();
  }, [fetchPaymentNotifications]);

  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [currentPage, pageCount]);

  const closeDetail = () => {
    setSelected(null);
    setActionError(null);
    setActionLoading(false);
  };

  /** PATCH notification as actioned — adjust URL/body if backend differs. */
  const handleMarkActioned = async () => {
    if (!selected?.id) return;
    setActionError(null);
    setActionLoading(true);
    const headers = {
      Authorization: `JWT ${authCtx?.token}`,
      "Acting-as": "dealer",
      "Content-Type": "application/json",
    };
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/dealer/investec-notifications/`,
        {
          is_actioned: true,
          id: selected.id
        },
        { headers }
      );
      const merged = { ...selected, is_actioned: true, ...(data && typeof data === "object" ? data : {}) };
      setSelected(merged);
      if (activeTab === TAB_INCOMPLETE) {
        setItems((prev) => prev.filter((r) => r.id !== selected.id));
      } else {
        setItems((prev) =>
          prev.map((r) => (r.id === selected.id ? { ...r, ...merged } : r))
        );
      }
    } catch (err) {
      console.error(err);
      setActionError(
        err.response?.data?.detail ||
        err.message ||
        "Could not mark notification as actioned."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const showMarkActionedButton =
    selected != null && selected.is_actioned !== true;

  const emptyMessage = (() => {
    if (debouncedSearch) {
      return `No payment notifications match “${debouncedSearch}”.`;
    }
    return activeTab === TAB_COMPLETED
      ? "No completed payment notifications found."
      : "No incomplete payment notifications found.";
  })();

  return (
    <>
      <Header title="Payment Notification" />
      <div className={styles.wrap}>
        <div className={styles.tabs} role="tablist" aria-label="Notification status">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_INCOMPLETE}
            className={`${styles.tab} ${
              activeTab === TAB_INCOMPLETE ? styles.tabActive : ""
            }`}
            onClick={() => handleTabChange(TAB_INCOMPLETE)}
          >
            Incomplete
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_COMPLETED}
            className={`${styles.tab} ${
              activeTab === TAB_COMPLETED ? styles.tabActive : ""
            }`}
            onClick={() => handleTabChange(TAB_COMPLETED)}
          >
            Completed
          </button>
        </div>
        <div className={styles.toolbar}>
          <p className={styles.meta}>
            {loading
              ? "Loading…"
              : `${sortedItems.length} notification${
                  sortedItems.length === 1 ? "" : "s"
                }`}
          </p>
          <label className={styles.searchField}>
            <span className={styles.searchLabel}>Filter</span>
            <input
              type="search"
              className={styles.searchInput}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Beneficiary, ordering customer, or sender ref…"
              aria-label="Filter payment notifications"
            />
          </label>
        </div>
        {error && <p className={styles.error}>{error}</p>}
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : sortedItems.length === 0 ? (
          <p className={styles.empty}>{emptyMessage}</p>
        ) : (
          <>
          <div className={styles.tableWrap}>
            <Table hover responsive className="modern-table">
              <thead>
                <tr>
                  {PAYMENT_NOTIFICATION_TABLE_COLUMNS.map(({ key, label }) => (
                    <th key={key}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((row) => (
                  <tr key={row.id} onClick={() => setSelected(row)}>
                    {PAYMENT_NOTIFICATION_TABLE_COLUMNS.map(({ key }) => (
                      <td key={key}>
                        {key === "is_actioned" ? (
                          <ActionedTableBadge value={row[key]} />
                        ) : key === "notes" ? (
                          <NotesIndicator count={getNotesCount(row, noteCountsById)} />
                        ) : (
                          formatCellValue(key, row[key])
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className={styles.pagination}>
            <span className={styles.paginationMeta}>
              Showing {pageStart}–{pageEnd} of {sortedItems.length}
            </span>
            <div className={styles.paginationControls}>
              <span className={styles.paginationMeta}>
                Page {currentPage} of {pageCount}
              </span>
              <button
                type="button"
                className="btn btn-secondary pagination-nav noselect"
                disabled={currentPage === 1}
                onClick={() =>
                  setCurrentPage((page) => Math.max(1, page - 1))
                }
              >
                {"<"}
              </button>
              <button
                type="button"
                className="btn btn-secondary pagination-nav noselect"
                disabled={currentPage === pageCount}
                onClick={() =>
                  setCurrentPage((page) => Math.min(pageCount, page + 1))
                }
              >
                {">"}
              </button>
            </div>
          </div>
          </>
        )}
      </div>

      <Modal
        dialogClassName={styles.modalDialogWide}
        show={selected != null}
        animation={false}
        onHide={closeDetail}
        centered
      >
        <Modal.Header closeButton={false} className={styles.modalHeader}>
          {selected ? (
            <Title as="h3" style={{ marginBottom: 0, fontSize: "1.15rem" }}>
              <FiBell size={18} />
              Notification details
            </Title>
          ) : null}
        </Modal.Header>
        <Modal.Body className={styles.modalBodyFlush}>
          {selected && (
            <ModalBodyPanel>
              <Container style={{ maxWidth: "100%", margin: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                <ModalCard>
                  <CardHeader>
                    <Left>
                      <PairBadge>{amountBadgeText(selected)}</PairBadge>
                      <Meta title="Created at">
                        <FiClock /> {formatClockLabel(selected)}
                      </Meta>
                    </Left>
                    <Right>
                      <Meta>Type</Meta>
                      <Strong>
                        {selected.message_type
                          ? String(selected.message_type)
                          : "—"}
                      </Strong>
                      <Meta>Actioned</Meta>
                      <Strong>
                        <ActionedTableBadge value={selected.is_actioned} />
                      </Strong>
                    </Right>
                  </CardHeader>
                  <div className={styles.detailLayout}>
                    <Body className={styles.detailMain}>
                      {PAYMENT_NOTIFICATION_DETAIL_SECTIONS.map((section) => (
                        <DetailSection key={section.title}>
                          <SectionTitle>{section.title}</SectionTitle>
                          <Grid>
                            {section.fields.map(({ key, label }) => (
                              <Row key={key}>
                                <Label>{label}</Label>
                                <Value>
                                  {key === "is_actioned" ? (
                                    <ActionedTableBadge
                                      value={selected[key]}
                                    />
                                  ) : (
                                    formatCellValue(key, selected[key])
                                  )}
                                </Value>
                              </Row>
                            ))}
                          </Grid>
                        </DetailSection>
                      ))}
                    </Body>
                    <aside className={styles.notesPanel}>
                      <PaymentNotificationNotes
                        notificationId={selected.id}
                        token={authCtx?.token}
                        username={authCtx?.username}
                        onNotesChange={(count) =>
                          handleNotesChange(selected.id, count)
                        }
                      />
                    </aside>
                  </div>
                </ModalCard>
              </Container>
            </ModalBodyPanel>
          )}
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <div className={styles.modalFooterInner}>
            {actionError ? (
              <p className={styles.actionError}>{actionError}</p>
            ) : null}
            <ModalActions>
              <Button
                variant="outline-secondary"
                onClick={closeDetail}
                disabled={actionLoading}
              >
                Close
              </Button>
              {showMarkActionedButton ? (
                <Button
                  variant="success"
                  onClick={handleMarkActioned}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Saving…" : "Mark as actioned"}
                </Button>
              ) : null}
            </ModalActions>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PaymentNotification;
